import { Alert, Box, Center, Group, Loader, Stack, Table, Text } from '@mantine/core';
import { SectionTitle } from './TitleSection';
import { BadgeBlock } from '../../components/BadgeBlock';
import { resolveProductSections, type ProductSections } from '../../lib/productSections';
import { useEffect, useState } from 'react';
import { fetchMe, MeFetchError } from '../../api/me';
import type { State } from '../EnterpriseHub';
import { useAuth } from '@workos-inc/authkit-react';
import type { ProductEntitlement } from '../../types/entitlements';
import { isOrgAdmin } from '../../lib/permissions';

const pageData = {
  title: 'Entitlements',
  subtitle: 'Licensed products, status, and renewal dates for your organisation.',
}

const badgeColor = (status: string) => {
  let color = 'var(--text-muted)';
  switch (status) {
    case 'active':
      color = 'var(--status-success)';
      break;
    case 'available':
      color = 'var(--status-warning)';
      break;
  }
  return color;
}

function formatRenewalDate(data?: ProductEntitlement): string {
  const iso = data?.trialEndsAt ?? data?.renewalDate;
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function ProfileEntitlements() {
  const auth = useAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [products, setProducts] = useState<ProductSections | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const token = await auth.getAccessToken();
        if (!token) {
          setState({ kind: 'error', status: 0 });
          return;
        }
        const data = await fetchMe(token, ctrl.signal);
        setState({ kind: 'ready', data });
        setProducts(resolveProductSections(data.products, isOrgAdmin(auth.permissions)));
      } catch (e) {
        if (e instanceof MeFetchError) setState({ kind: 'error', status: e.status });
        else if ((e as Error).name !== 'AbortError') setState({ kind: 'error', status: 0 });
      }
    })();
    return () => ctrl.abort();
  }, [auth]);



  if (!products) return null;

  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} gap={'lg'} px={32} py={24}>
      <SectionTitle {...pageData} />

      {state.kind === 'loading' && <LoadingState />}
      {state.kind === 'ready' && <EntitlementsTable products={products} />}
      {state.kind === 'error' && <ErrorState />}



    </Stack>
  );
}


function LoadingState() {
  return (
    <Center flex={1} >
      <Loader></Loader>
    </Center>
  );
}
function ErrorState() {
  return (
    <Alert
      color="var(--status-danger)"
      variant="light"
      p={'xl'}
      ta={'center'}
      style={{
        borderColor: 'var(--status-danger-background)',
        '--alert-bg': 'var(--status-danger-background)',
      }}
    >
      <Text c={'var(--status-danger)'} size="sm">
        We couldn't reach the Cloudhouse account service. Please refresh in a moment.
      </Text>
    </Alert>
  );
}

function EntitlementsTable({ products }: { products: ProductSections | null }) {
  // The Entitlements tab lists only products the org actually holds an
  // entitlement for. resolveProductSections also surfaces no-entitlement
  // products as cross-sell (for the home grid), so drop those here.
  const data = products ? [...products.active, ...products.available].filter(([, ent]) => ent) : [];
  return (
    <Box
      id="users-table"
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        background: 'var(--bg-surface)',
        flex: 'none',

      }}
    >
      <Table highlightOnHover={!!data.length} style={{
        '--table-vertical-spacing': '14px 18px',
        '--table-border-color': 'var(--border-default)',
        '--table-hover-color': 'var(--bg-subtle)',

      }}>
        <Table.Thead>
          <Table.Tr style={{
            '--table-vertical-spacing': '11px 18px',
            color: 'var(--text-subtle)',
            fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase',
            background: 'var(--bg-subtle)',
          }}>
            <Table.Th>Product</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Renewal / trial end</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>

          {data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Stack align="center" py={'xl'}>
                  <Text fz="sm" fw={600} c="var(--text-subtle)">No products found</Text>
                </Stack>
              </Table.Td>
            </Table.Tr>
          )}

          {data.map(([product, data]) => (
            <Table.Tr key={product.key}>
              <Table.Td>
                <Group gap={11} wrap="nowrap">
                  <Stack w={34}>
                    <img src={product.logo} alt={product.name} style={{ width: '100%', height: 'auto' }} />
                  </Stack>
                  <Text fz="sm" fw={600} lh="normal">{product.name}</Text>
                </Group>
              </Table.Td>

              <Table.Td>
                <Text fz="sm" lh="normal">{product.tagline}</Text>

              </Table.Td>

              <Table.Td>
                {data?.plan === 'trial' ? (
                  <BadgeBlock fz={'xs'} bdrs={'xl'} color="var(--status-info)" display={'flex'}>
                    Trial
                  </BadgeBlock>
                ) : (
                  data?.status && <BadgeBlock fz={'xs'} bdrs={'xl'} tt={'capitalize'} color={badgeColor(data.status)} display={'flex'}>
                    {data?.status}
                  </BadgeBlock>
                )}
              </Table.Td>

              <Table.Td>
                <Text fz="sm" c="var(--text-default)">{formatRenewalDate(data)}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
